import { useCallback, useMemo, useState } from 'react';

import {
  ChakraBox,
  ChakraVStack,
  FAIconName,
  FireflyButton,
  Popover,
} from '@equip.health/ui';

import FilterBuilder from './InquiryFilterBuilder';
import {
  InquiryFilterConfig,
  InquiryFilterKeys,
  InquiryFilterTypes,
  InquiryFilterValue,
  InquiryPSRFilterKeys,
  InquiryPSRFilterNameMapper,
  PSRFilterKeys,
  PSRInitialFilterElementMapper,
} from '~/lib/constants/inquiry.constants';
import {
  InquiryFilterDropdownMenuOptionsMapper,
  getFilterValues,
} from '~/lib/util/inquiry.util';
import { useResetFiltersOnDefaultChange } from '~/lib/hooks/useResetFiltersOnDefaultChange';

import './InquiryFilter.css';
import { useFlags } from 'launchdarkly-react-client-sdk';

const TOP_LEVEL_ID = 'inquiry-filter-popover';

const InquiryPSRFilter = ({
  admissionsSupervisors,
  channels,
  channelTypes,
  targetTreatmentPrograms,
  defaultFilters = [],
  informationSources,
  isOpen,
  onClose,
  onSaveFilters,
  patientServiceRepresentatives,
  qcAppointments,
  referralCoordinators,
  timezones,
  maxHeight,
}: InquiryPSRFilterProps) => {
  const {
    inquiryListViewFilterByTreatmentProgram:
      isInquiryListViewFilterByTreatmentProgramEnabled,
  } = useFlags<LDFlags>();

  const [filterValues, setFilterValues] = useState<InquiryFilterValue[]>(
    () =>
      Object.values({ ...InquiryFilterKeys, ...PSRFilterKeys }).reduce(
        (acc, key) => ({
          ...acc,
          [key]: PSRInitialFilterElementMapper(defaultFilters)[key],
        }),
        {},
      ) as InquiryFilterValue[],
  );

  const onSubmitFilters = () => {
    onClose();
    const validFilters = Object.entries(filterValues).reduce(
      (acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value; // Only include defined filters
        }
        return acc;
      },
      {} as typeof filterValues,
    );

    onSaveFilters(
      Object.entries(validFilters).flatMap(([key, value]) =>
        getFilterValues(value, key as InquiryPSRFilterKeys),
      ),
    );
  };

  const onResetFilters = useCallback(() => {
    const initialFilterValues = Object.values({
      ...InquiryFilterKeys,
      ...PSRFilterKeys,
    }).reduce((acc, key) => {
      const initialValue = PSRInitialFilterElementMapper(defaultFilters)[key];
      const isMinMaxField =
        typeof initialValue === 'object' &&
        initialValue !== null &&
        'min' in initialValue &&
        'max' in initialValue;

      if (isMinMaxField) {
        acc[key] = {
          max: initialValue.max ?? '',
          min: initialValue.min ?? '',
        };
      } else {
        acc[key] = initialValue ?? '';
      }

      return acc;
    }, {}) as InquiryFilterValue[];

    setFilterValues(() => initialFilterValues);
  }, [defaultFilters]);

  useResetFiltersOnDefaultChange(defaultFilters, onResetFilters);

  // update filter values on input change
  const onFilterChange = (
    key: InquiryPSRFilterKeys,
    newValue: InquiryFilterValue,
  ): void => {
    if (newValue === undefined) return; // Skip undefined filters
    setFilterValues((prev) => ({
      ...prev,
      [key]: newValue,
    }));
  };

  const defaultFilterConfig = useCallback(
    (key: InquiryFilterKeys | PSRFilterKeys): InquiryFilterConfig => {
      return {
        defaultValue: filterValues[key],
        isSingleSelect: false,
        key,
        label: InquiryPSRFilterNameMapper[key],
        placeholder: '',
        type: null,
      };
    },
    [filterValues],
  );

  const PSRFilterConfigs: InquiryFilterConfig[] = useMemo(
    () => [
      {
        ...defaultFilterConfig(InquiryFilterKeys.INQUIRY_STATUS),
        type: InquiryFilterTypes.STATUS,
      },
      {
        ...defaultFilterConfig(PSRFilterKeys.PSR_EXTERNAL_ID),
        providerFilterData: patientServiceRepresentatives,
        type: InquiryFilterTypes.PROVIDER,
      },
      {
        ...defaultFilterConfig(PSRFilterKeys.RC_EXTERNAL_ID),
        providerFilterData: referralCoordinators,
        type: InquiryFilterTypes.PROVIDER,
      },
      {
        ...defaultFilterConfig(PSRFilterKeys.ADMISSIONS_SUPERVISOR_EXTERNAL_ID),
        providerFilterData: admissionsSupervisors,
        type: InquiryFilterTypes.PROVIDER,
      },
      {
        ...defaultFilterConfig(InquiryFilterKeys.LAST_CONTACT_DATE_MAX),
        label: 'Days since last contact',
        minMax: {
          max: {
            defaultValue: filterValues[InquiryFilterKeys.LAST_CONTACT_DATE_MAX],
            key: InquiryFilterKeys.LAST_CONTACT_DATE_MAX,
            label:
              InquiryPSRFilterNameMapper[
                InquiryFilterKeys.LAST_CONTACT_DATE_MAX
              ],
          },
          min: {
            defaultValue: filterValues[InquiryFilterKeys.LAST_CONTACT_DATE_MIN],
            key: InquiryFilterKeys.LAST_CONTACT_DATE_MIN,
            label:
              InquiryPSRFilterNameMapper[
                InquiryFilterKeys.LAST_CONTACT_DATE_MIN
              ],
          },
        },
        type: InquiryFilterTypes.MIN_MAX,
      },
      ...(isInquiryListViewFilterByTreatmentProgramEnabled
        ? [
            {
              ...defaultFilterConfig(
                InquiryFilterKeys.TARGET_TREATMENT_PROGRAM,
              ),
              options: InquiryFilterDropdownMenuOptionsMapper(
                targetTreatmentPrograms,
              ),
              type: InquiryFilterTypes.SELECT,
            },
          ]
        : []),
      {
        ...defaultFilterConfig(InquiryFilterKeys.TIMEZONE),
        options: InquiryFilterDropdownMenuOptionsMapper(timezones),
        type: InquiryFilterTypes.SELECT,
      },
      {
        ...defaultFilterConfig(PSRFilterKeys.IS_QC_SCHEDULED),
        isSingleSelect: true,
        options: InquiryFilterDropdownMenuOptionsMapper(qcAppointments),
        type: InquiryFilterTypes.SELECT,
      },
      {
        ...defaultFilterConfig(InquiryFilterKeys.CHANNEL),
        options: channels.map((option) => ({
          color: option.color,
          label: option.channel,
          value: option.channel,
        })),
        type: InquiryFilterTypes.SELECT,
      },
      {
        ...defaultFilterConfig(InquiryFilterKeys.CHANNEL_TYPE),
        options: InquiryFilterDropdownMenuOptionsMapper(channelTypes),
        type: InquiryFilterTypes.SELECT,
      },
      {
        ...defaultFilterConfig(PSRFilterKeys.INFORMATION_SOURCE),
        options: informationSources,
        type: InquiryFilterTypes.SELECT,
      },
      {
        ...defaultFilterConfig(InquiryFilterKeys.INQUIRY_DATE_MIN),
        minMax: {
          max: {
            defaultValue: filterValues[InquiryFilterKeys.INQUIRY_DATE_MAX],
            key: InquiryFilterKeys.INQUIRY_DATE_MAX,
            label:
              InquiryPSRFilterNameMapper[InquiryFilterKeys.INQUIRY_DATE_MAX],
          },
          min: {
            defaultValue: filterValues[InquiryFilterKeys.INQUIRY_DATE_MIN],
            key: InquiryFilterKeys.INQUIRY_DATE_MIN,
            label:
              InquiryPSRFilterNameMapper[InquiryFilterKeys.INQUIRY_DATE_MIN],
          },
        },
        type: InquiryFilterTypes.DATE_RANGE,
      },
    ],
    [
      admissionsSupervisors,
      channelTypes,
      channels,
      defaultFilterConfig,
      filterValues,
      informationSources,
      patientServiceRepresentatives,
      qcAppointments,
      referralCoordinators,
      timezones,
      targetTreatmentPrograms,
    ],
  );

  return (
    <Popover
      bodyContent={
        <ChakraVStack>
          <FilterBuilder
            filters={PSRFilterConfigs}
            id={TOP_LEVEL_ID}
            onChange={onFilterChange}
          />
          <ChakraBox
            backgroundColor="white"
            bottom="0"
            paddingLeft="20px"
            paddingRight="20px"
            position="sticky"
            width="100%"
          >
            <FireflyButton
              id={`${TOP_LEVEL_ID}__save-filters`}
              isFullWidth
              leftIcon={FAIconName.checkRegular}
              onClick={onSubmitFilters}
            >
              Save filters
            </FireflyButton>
          </ChakraBox>
        </ChakraVStack>
      }
      // We specifically want content padding on the bottom to prevent the scroll to appear below the Save button.
      bodyPadding="20px 0px 0px 0px"
      contentPadding="0px 0px 20px 0px"
      id={TOP_LEVEL_ID}
      isOpen={isOpen}
      maxHeight={maxHeight ?? 'calc(100vh - 75px)'}
      minWidth="360px"
      onClose={() => {
        onResetFilters();
        onClose();
      }}
      size="md"
    />
  );
};

export default InquiryPSRFilter;
